import { motion } from "framer-motion";
import { AlertTriangle, Thermometer, Layers, Palette } from "lucide-react";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
const heroImg = "/images/site/stair-nosing-inlay-detail-dark-stained-landing.webp";
import usePageSEO from "@/hooks/usePageSEO";
import PageBreadcrumb from "@/components/PageBreadcrumb";

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
};

const ProductSpecification = () => {
  usePageSEO({
    title: "Hardwood Flooring Specification for Architects & Designers",
    description: "Hardwood flooring specification consulting for architects, designers, and builders. Solid vs engineered, finish chemistry, moisture mechanics, NWFA standards.",
    path: "/product-specification",
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>

      {/* ══ 1 · HERO ══ */}
      <section className="relative h-[70svh] min-h-[480px] max-h-[720px] flex items-end overflow-hidden">
        <PageBreadcrumb variant="overlay" items={[
          { label: "Home", to: "/" },
          { label: "Services" },
          { label: "Product Specification" },
        ]} />
        <div className="absolute inset-0">
          <img width={1920} height={1080} loading="eager" decoding="async" fetchPriority="high"
            src={heroImg}
            alt="Macro close-up of hardwood end-grain cross-section"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/40 to-foreground/10" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl"
        >
          <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary mb-3 md:mb-4 flex items-center gap-2">
            <span className="w-7 h-px bg-primary inline-block" />
            Product Specification · Metro Detroit, Michigan
          </p>
          <h1 className="font-display text-[clamp(30px,6.5vw,72px)] font-semibold leading-[1.05] tracking-tight text-primary-foreground">
            The Hardwood Flooring Specification Guide
          </h1>
          <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
            The right floor in the wrong environment will fail. We cut through the marketing jargon to engineer the perfect product for your home's unique subfloor, lifestyle, and HVAC system.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
            <QuoteModal
              trigger={
                <button className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center">
                  Consult a Specifier →
                </button>
              }
            />
            <a
              href="tel:2483258853"
              className="border border-primary-foreground/25 text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors text-center"
            >
              Call (248) 325-8853
            </a>
          </div>
        </motion.div>
      </section>

      {/* ══ 2 · SOLID vs ENGINEERED ══ */}
      <section className="bg-foreground py-16 md:py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp} className="max-w-3xl mx-auto mb-12 md:mb-16">
            <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-3 flex items-center gap-2">
              <span className="w-7 h-px bg-primary inline-block" />
              The Scientific Reality
            </p>
            <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-primary-foreground leading-[1.1] mb-6">
              Shattering the Myths:{" "}
              <em className="italic font-normal text-[hsl(var(--gold-300))]">Solid vs. Engineered.</em>
            </h2>
            <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/60 leading-[1.85] md:leading-[1.9]">
              There is a massive misconception that one type of flooring is universally "better" than the other. The truth is, both solid and engineered floors are exceptional. But only when specified for the correct environment. Neither is a magic solution that allows you to ignore your home's climate.
            </p>
          </motion.div>

          {/* The Humidity Rule */}
          <motion.div {...fadeUp} className="max-w-3xl mx-auto mb-8 md:mb-10">
            <div className="border border-primary/20 bg-primary/5 rounded-2xl p-7 md:p-9">
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-3 flex items-center gap-2">
                <span className="w-5 h-px bg-primary inline-block" />
                The Humidity Rule
              </p>
              <p className="text-[14px] md:text-[16px] font-light text-primary-foreground/75 leading-[1.85]">
                Whether you choose a solid plank or an engineered core, both products strictly require an indoor Relative Humidity (RH) between{" "}
                <span className="text-primary font-semibold">30% and 50%</span> year-round to survive.
              </p>
            </div>
          </motion.div>

          {/* Comparison Blocks */}
          <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 md:mb-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="border border-primary-foreground/10 rounded-2xl p-7 md:p-8 group hover:border-primary/30 transition-colors duration-500"
            >
              <span className="text-[11px] font-semibold tracking-[.14em] uppercase text-primary-foreground/40 mb-4 block">How Solid Wood Reacts</span>
              <p className="text-[12px] md:text-[13px] font-light text-primary-foreground/55 leading-[1.85]">
                Solid wood is highly reactive. If it takes on too much moisture, the planks will <span className="text-primary-foreground/80 font-medium">expand and cup</span>. If the environment drops below 30% humidity in the winter, the wood will dry out, shrink, and cause noticeable <span className="text-primary-foreground/80 font-medium">gaps or surface checking</span> (cracks).
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="border border-primary-foreground/10 rounded-2xl p-7 md:p-8 group hover:border-primary/30 transition-colors duration-500"
            >
              <span className="text-[11px] font-semibold tracking-[.14em] uppercase text-primary-foreground/40 mb-4 block">How Engineered Wood Reacts</span>
              <p className="text-[12px] md:text-[13px] font-light text-primary-foreground/55 leading-[1.85]">
                Engineered wood is designed with cross-directional layers to restrict normal expansion, making it generally more dimensionally stable. However, it is not invincible. If an engineered floor gets too dry, it can experience <span className="text-primary-foreground/80 font-medium">"dry cupping"</span> where the face layer and the core contract at different rates. This leads to severe <span className="text-primary-foreground/80 font-medium">face checking and delamination</span>.
              </p>
            </motion.div>
          </div>

          {/* Radiant Heat Factor */}
          <motion.div {...fadeUp} className="max-w-3xl mx-auto">
            <div className="border-l-2 border-primary/40 pl-6 md:pl-8 py-1">
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-[hsl(var(--gold-300))] mb-3">
                The Radiant Heat Factor
              </p>
              <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/55 leading-[1.85]">
                If your home utilizes radiant floor heating, the specification process becomes even more rigorous. While you can use dimensionally stable species (like Quarter-Sawn White Oak) in either solid or engineered formats, we must verify that the specific manufacturer <span className="text-primary font-medium">explicitly warranties their product for radiant heat applications</span>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ 3 · THE SPECTRUM OF QUALITY ══ */}
      <section className="bg-background py-14 md:py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          {/* Section Header */}
          <motion.div {...fadeUp} className="mb-12 md:mb-16 max-w-3xl">
            <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-[hsl(var(--gold-400))] mb-2 flex items-center gap-2">
              Anatomy of Quality
              <span className="flex-1 h-px bg-border" />
            </p>
            <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-foreground leading-[1.1]">
              The Spectrum of Quality:{" "}
              <em className="italic font-normal text-primary">Understanding Engineered Grades.</em>
            </h2>
            <p className="mt-5 text-sm md:text-[15px] font-light text-muted-foreground leading-[1.85]">
              Engineered hardwood is not a single category. It spans a wide spectrum of quality. While entry-level engineered floors offer accessible price points and quick click lock installations, custom European-imported wide planks represent the pinnacle of architectural design. The price and performance differences come down to two primary structural factors: the cut of the face veneer and the composition of the core.
            </p>
          </motion.div>

          {/* Deep Dive Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Factor 1: The Face Veneer */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="border border-border/50 rounded-2xl p-8 md:p-10 flex flex-col"
            >
              <span className="text-[56px] md:text-[64px] font-display font-bold text-primary/10 leading-none mb-2">01</span>
              <h3 className="font-display text-xl md:text-2xl font-semibold text-foreground leading-snug mb-5">
                The Face Veneer:{" "}
                <em className="italic font-normal text-primary">Rotary Peeled vs. Dry Sawn.</em>
              </h3>
              <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-[1.85] mb-5">
                How the top layer of wood is harvested dictates the floor's aesthetic and lifespan.
              </p>

              <div className="space-y-4 mb-6">
                <div className="border-l-2 border-muted-foreground/25 pl-5">
                  <p className="text-[11px] font-semibold tracking-[.12em] uppercase text-muted-foreground/70 mb-1">
                    <span className="text-primary font-medium">Rotary Peeled</span>
                  </p>
                  <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-[1.85]">
                    A highly efficient manufacturing method where the log is peeled in a continuous sheet. This maximizes yield and keeps costs low, but produces a thinner veneer with a repetitive, wide grain pattern.
                  </p>
                </div>

                <div className="border-l-2 border-muted-foreground/35 pl-5">
                  <p className="text-[11px] font-semibold tracking-[.12em] uppercase text-muted-foreground/70 mb-1">Sliced Face</p>
                  <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-[1.85]">
                    A mid-tier option yielding a 1.5mm to 3mm thickness, offering a more natural grain appearance.
                  </p>
                </div>

                <div className="border-l-2 border-primary/50 pl-5">
                  <p className="text-[11px] font-semibold tracking-[.12em] uppercase text-primary mb-1">Dry Sawn  Architectural Grade</p>
                  <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-[1.85]">
                    For our clients, we specify premium floors that are sawn just like traditional solid lumber, yielding a substantial{" "}
                    <span className="text-primary font-medium">4mm to 6mm wear layer</span>.
                  </p>
                </div>
              </div>

              {/* Refinishing Standard Callout */}
              <div className="mt-auto border border-primary/15 bg-primary/5 rounded-xl p-5 md:p-6">
                <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2 flex items-center gap-2">
                  <span className="w-4 h-px bg-primary inline-block" />
                  The Refinishing Standard
                </p>
                <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-[1.85]">
                  A substantial dry-sawn face allows the floor to be sanded and refinished multiple times. Furthermore, applying water-based finishes to very thin veneers can sometimes induce cupping due to the moisture. For anything under 4mm, we often recommend penetrating{" "}
                  <span className="text-primary font-medium">Hardwax Oils</span>. For true generational longevity, a{" "}
                  <span className="text-primary font-medium">4mm+ sawn face</span> is the industry gold standard.
                </p>
              </div>
            </motion.div>

            {/* Factor 2: The Core */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.7 }}
              className="border border-border/50 rounded-2xl p-8 md:p-10 flex flex-col"
            >
              <span className="text-[56px] md:text-[64px] font-display font-bold text-primary/10 leading-none mb-2">02</span>
              <h3 className="font-display text-xl md:text-2xl font-semibold text-foreground leading-snug mb-5">
                The Core:{" "}
                <em className="italic font-normal text-primary">HDF vs. Baltic Birch Plywood.</em>
              </h3>
              <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-[1.85] mb-6">
                The foundation beneath the veneer dictates how the floor handles environmental stress.
              </p>

              {/* HDF/MDF Cores */}
              <div className="border border-border/40 rounded-xl p-5 md:p-6 mb-5">
                <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-muted-foreground/60 mb-2 flex items-center gap-2">
                  <span className="w-4 h-px bg-muted-foreground/40 inline-block" />
                  Entry &amp; Mid-Grade
                </p>
                <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-[1.85]">
                  <span className="text-primary font-medium">HDF/MDF</span> (High-Density Fiberboard) cores are common in entry-level and mid-grade floors. While they are dense and structurally consistent, they are more sensitive to moisture fluctuations and require extremely strict climate control.
                </p>
              </div>

              {/* Baltic Birch */}
              <div className="border border-primary/20 bg-primary/5 rounded-xl p-5 md:p-6 flex-1">
                <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2 flex items-center gap-2">
                  <span className="w-4 h-px bg-primary inline-block" />
                  Architectural Grade
                </p>
                <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-[1.85]">
                  For maximum stability in Michigan's extreme humidity swings, we specify{" "}
                  <span className="text-primary font-medium">11-ply Baltic Birch marine-grade plywood cores</span>. Whether the total thickness is ½″, ⅝″, or a massive ¾″, the cross-directional engineering of{" "}
                  <span className="text-primary font-medium">Baltic Birch</span> provides supreme structural integrity and dimensional stability.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ 3b · HOME ECOSYSTEM ASSESSMENT ══ */}
      <section className="bg-foreground py-16 md:py-24 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp} className="mb-10 md:mb-14 max-w-3xl">
            <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-3 flex items-center gap-2">
              <span className="w-7 h-px bg-primary inline-block" />
              Environmental Assessment
            </p>
            <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-primary-foreground leading-[1.1] mb-5">
              Beyond the Plank: Assessing Your Home's{" "}
              <em className="italic font-normal text-[hsl(var(--gold-300))]">Ecosystem.</em>
            </h2>
            <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/55 leading-[1.85]">
              A hardwood floor is a living, breathing architectural element. It constantly reacts to its environment. Before we ever recommend a product, we perform a 360-degree assessment of your home's thermal envelope and mechanical systems.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                icon: Thermometer,
                title: "HVAC & Climate Control",
                desc: "How is your home heated and cooled? Radiant floor heating requires highly dimensionally stable engineered wood, while forced-air systems with whole-home humidification offer different flexibility. We match the wood's performance limits directly to your climate systems.",
              },
              {
                icon: Layers,
                title: "Foundation & Substructure",
                desc: "Moisture is the enemy of wood. We evaluate what lies beneath: Are we installing over a moisture-emitting concrete slab, a conditioned crawlspace, a finished basement, or a second-story wood subfloor? Each foundation type demands a specific core construction and moisture mitigation strategy.",
              },
              {
                icon: Palette,
                title: "The 'Visual Match' Strategy",
                desc: "You never have to sacrifice aesthetics for performance. Many premium manufacturers produce the exact same visual finish in both solid and engineered formats. We can specify a solid plank for your main level, and the exact matching engineered plank for your basement, ensuring a seamless aesthetic throughout your entire home.",
              },
            ].map((pillar, i) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="border border-primary-foreground/10 rounded-2xl p-7 md:p-8 flex flex-col group hover:border-primary/30 hover:bg-primary-foreground/[.03] transition-all duration-500"
              >
                <pillar.icon className="w-7 h-7 text-primary mb-5 group-hover:text-[hsl(var(--gold-300))] transition-colors duration-500" strokeWidth={1.5} />
                <h4 className="font-display text-lg md:text-xl font-semibold text-primary-foreground mb-3 leading-snug">
                  {pillar.title}
                </h4>
                <p className="text-[12px] md:text-[13px] font-light text-primary-foreground/50 leading-relaxed group-hover:text-primary-foreground/65 transition-colors duration-500">
                  {pillar.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 4 · THE CONCRETE PUZZLE ══ */}
      <section className="bg-foreground py-14 md:py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div
            {...fadeUp}
            className="border border-primary/25 bg-primary/[.04] rounded-2xl p-8 md:p-12 relative overflow-hidden"
          >
            <div className="absolute top-6 right-6 md:top-8 md:right-8">
              <AlertTriangle className="w-10 h-10 md:w-14 md:h-14 text-primary/15" />
            </div>

            <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-3 flex items-center gap-2">
              <span className="w-5 h-px bg-primary inline-block" />
              Inspector's Note
            </p>
            <h2 className="font-display text-[clamp(24px,4vw,42px)] font-semibold text-primary-foreground leading-[1.1] mb-8">
              The "Below-Grade" Rule:{" "}
              <em className="italic font-normal text-[hsl(var(--gold-300))]">Solid Wood &amp; Concrete.</em>
            </h2>

            {/* Below-Grade */}
            <div className="space-y-6 max-w-3xl">
              <div className="border-l-2 border-primary/40 pl-6 md:pl-8">
                <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary/80 mb-2">The Reality of Basements  Below-Grade</p>
                <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/60 leading-[1.85]">
                  Concrete is porous and constantly emits moisture vapor. Installing solid hardwood below-grade (in a basement) is universally <span className="text-primary font-medium">un-warrantied by manufacturers</span> and falls outside <span className="text-primary font-medium">NWFA guidelines</span>. If you desire the aesthetic of real hardwood in a basement, a premium <span className="text-primary-foreground/80 font-medium">engineered wood floor</span> is the scientifically correct specification.
                </p>
              </div>

              {/* On-Grade */}
              <div className="border-l-2 border-[hsl(var(--gold-300))]/40 pl-6 md:pl-8">
                <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-[hsl(var(--gold-300))]/80 mb-2">On-Grade &amp; Above-Grade Slabs</p>
                <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/60 leading-[1.85]">
                  However, not all concrete is in the basement. Solid hardwood can be successfully and beautifully installed over on-grade or above-grade concrete slabs, provided the foundation is properly engineered. This requires comprehensive <span className="text-primary font-medium">moisture testing</span> and the application of advanced <span className="text-primary font-medium">roll-on moisture vapor membranes</span>, or the installation of specialized <span className="text-primary font-medium">floating subfloor systems</span> (like DRIcore) to separate the wood from the slab.
                </p>
              </div>

              {/* Alternative Solutions */}
              <div className="border-l-2 border-primary-foreground/15 pl-6 md:pl-8">
                <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary-foreground/40 mb-2">Alternative Solutions</p>
                <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/60 leading-[1.85]">
                  Depending on the moisture readings of your slab and your budget, premium waterproof <span className="text-primary font-medium">LVP</span> (like <span className="text-primary font-medium">Cali Vinyl</span>) or high-performance <span className="text-primary font-medium">laminate</span> (like <span className="text-primary font-medium">Quick Step</span>) are also exceptional alternatives for below-grade applications.
                </p>
              </div>

              {/* The Takeaway */}
              <div className="border border-primary/15 bg-primary/[.03] rounded-xl p-5 md:p-7 mt-4">
                <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2 flex items-center gap-2">
                  <span className="w-4 h-px bg-primary inline-block" />
                  The Takeaway
                </p>
                <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/60 leading-[1.85]">
                  Every concrete slab is unique. The specification process involves evaluating moisture levels, elevation, and structural integrity to determine the best path forward. <span className="text-primary-foreground/80 font-medium">Contact us for a site evaluation</span>, and we will engineer the exact system your concrete foundation requires.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ 5 · CTA ══ */}
      <section className="bg-primary py-10 md:py-14">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-[clamp(24px,3vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-2">
              Don't buy a floor without consulting the{" "}
              <em className="italic font-normal text-[hsl(var(--gold-300))]">environment.</em>
            </h3>
            <p className="text-[13px] font-light text-primary-foreground/65 leading-relaxed">
              Let our NWFA Certified team evaluate your subfloor, measure your humidity, and specify a product guaranteed to last a lifetime.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full md:w-auto">
            <QuoteModal
              trigger={
                <button className="bg-primary-foreground text-primary px-7 py-3.5 rounded-xl text-xs font-bold tracking-[.05em] uppercase hover:-translate-y-px transition-transform whitespace-nowrap text-center">
                  Schedule a Site Evaluation →
                </button>
              }
            />
            <a
              href="tel:2483258853"
              className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-xl text-xs font-semibold tracking-[.05em] hover:border-primary-foreground hover:text-primary-foreground transition-colors whitespace-nowrap text-center"
            >
              📞 (248) 325-8853
            </a>
          </div>
        </div>
      </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default ProductSpecification;
